import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import React from 'react';
import { classNames } from '../_common/defaultImports';
import { ChevronDownIconOutline, ChevronLeftIconOutline, ChevronRightIconOutline, ChevronUpIconOutline, } from '../icons';
import { IconButton } from './IconButton';
const CollapsibleButtonWrapper = ({ children, }) => {
    return _jsx(_Fragment, { children: children });
};
CollapsibleButtonWrapper.displayName = 'CollapsibleButtonWrapper';
export const IconButtonArrayComponent = React.forwardRef(function IconButtonArrayComponent({ children, as, isFloating = false, orientation = 'horizontal', size = 'medium', className, style, htmlAttributes, }, ref) {
    const [isCollapsed, setIsCollapsed] = React.useState(true);
    const classes = classNames('ndl-icon-btn-array', {
        'n-flex-row': orientation === 'horizontal',
        'n-flex-col': orientation === 'vertical',
        'ndl-array-floating': isFloating,
        [`ndl-${size}`]: size,
        [className]: className,
    });
    const Component = as || 'div';
    const applySizeProp = (child) => {
        if (React.isValidElement(child) &&
            child.type === IconButton) {
            return React.cloneElement(child, { size: child.props.size || size });
        }
        return child;
    };
    const childrenArray = React.Children.toArray(children);
    const fixedButtons = childrenArray.filter((child) => !React.isValidElement(child) ||
        child.type.displayName !==
            'CollapsibleButtonWrapper');
    const fixedButtonsWithProps = React.Children.map(fixedButtons, applySizeProp);
    const collapsibleButtonWrapper = childrenArray.find((child) => React.isValidElement(child) &&
        child.type.displayName ===
            'CollapsibleButtonWrapper');
    const collapsibleButtonsWithProps = collapsibleButtonWrapper
        ? React.Children.map(collapsibleButtonWrapper.props.children, applySizeProp)
        : null;
    const renderChevronIcon = () => {
        if (orientation === 'horizontal') {
            return isCollapsed ? (_jsx(ChevronRightIconOutline, {})) : (_jsx(ChevronLeftIconOutline, {}));
        }
        return isCollapsed ? _jsx(ChevronDownIconOutline, {}) : _jsx(ChevronUpIconOutline, {});
    };
    return (_jsxs(Component, Object.assign({ role: "group", className: classes, ref: ref, style: style }, htmlAttributes, { children: [fixedButtonsWithProps, collapsibleButtonsWithProps && (_jsxs(_Fragment, { children: [!isCollapsed && collapsibleButtonsWithProps, _jsx(IconButton, { onClick: () => setIsCollapsed(!isCollapsed), size: size, ariaLabel: "Show more", "aria-expanded": !isCollapsed, isClean: true, isGrouped: true, children: renderChevronIcon() })] }))] })));
});
const IconButtonArray = Object.assign(IconButtonArrayComponent, {
    CollapsibleButtonWrapper,
});
IconButtonArray.displayName = 'IconButtonArray';
export { IconButtonArray };
//# sourceMappingURL=IconButtonArray.js.map